import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Snackbar, Alert, Paper, Divider, MenuItem,
  Switch, FormControlLabel, InputAdornment, IconButton,
} from "@mui/material";
import VisibilityIcon    from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { DataGrid }          from "@mui/x-data-grid";
import AdminPageHeader       from "../../components/admin/AdminPageHeader";
import AdminGridToolbar      from "../../components/admin/AdminGridToolbar";
import AdminFormDialog       from "../../components/admin/AdminFormDialog";
import AdminFilterPanel, { filterFieldSx } from "../../components/admin/AdminFilterPanel";
import {
  listIntegrationConfigs,
  createIntegrationConfig,
  updateIntegrationConfig,
  deleteIntegrationConfig,
  listAssignableCompanies,
} from "../../services/adminService";
import { ADMIN_PAPER_SX, ADMIN_GRID_SX } from "../../components/admin/adminGridStyles";

const DIRECTION_OPTIONS = ["entrada", "saida"];
const AUTH_TYPE_OPTIONS = ["none", "basic", "oauth2_client_credentials"];
const MASK = "••••••••";

const EMPTY = {
  name: "", description: "", direction: "saida", url: "",
  auth_type: "oauth2_client_credentials", token_url: "", client_id: "",
  client_secret: "", timeout_ms: 30000, is_active: true, domain: "",
  company_cnpj: "", rfc_function: "",
};

const authTypeLabel = (v) => ({
  none: "Sem autenticação",
  basic: "Basic Auth",
  oauth2_client_credentials: "OAuth2 Client Credentials",
}[v] ?? v);

export default function IntegrationConfigPage() {
  const [rows,       setRows]       = useState([]);
  const [companies,  setCompanies]  = useState([]);
  const [searchName, setSearchName] = useState("");
  const [loading,    setLoading]    = useState(false);
  const [rowSel,     setRowSel]     = useState({ type: "include", ids: new Set() });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(EMPTY);
  const [saving,     setSaving]     = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [snack,      setSnack]      = useState({ open: false, msg: "", severity: "success" });

  const selectedIds = useMemo(() => Array.from(rowSel.ids), [rowSel]);
  const selectedRow = useMemo(() => rows.find(r => r.id === selectedIds[0]), [rows, selectedIds]);

  const showSnack = (msg, severity = "success") => setSnack({ open: true, msg, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listIntegrationConfigs();
      setRows(Array.isArray(data) ? data : []);
    } catch { showSnack("Erro ao buscar configurações", "error"); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    listAssignableCompanies().then(d => setCompanies(Array.isArray(d) ? d : [])).catch(() => {});
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowSecret(false);
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!selectedRow) return;
    setEditing(selectedRow);
    setForm({
      name:          selectedRow.name,
      description:   selectedRow.description ?? "",
      direction:     selectedRow.direction,
      url:           selectedRow.url,
      auth_type:     selectedRow.auth_type,
      token_url:     selectedRow.token_url ?? "",
      client_id:     selectedRow.client_id ?? "",
      client_secret: selectedRow.client_secret ? MASK : "",
      timeout_ms:    selectedRow.timeout_ms,
      is_active:     selectedRow.is_active,
      domain:        selectedRow.domain ?? "",
      company_cnpj:  selectedRow.company_cnpj ?? "",
      rfc_function:  selectedRow.rfc_function ?? "",
    });
    setShowSecret(false);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.direction || !form.url)
      return showSnack("Nome, direção e URL são obrigatórios", "error");

    setSaving(true);
    try {
      const body = {
        ...form,
        timeout_ms: Number(form.timeout_ms) || 30000,
        token_url:  form.token_url  || null,
        client_id:  form.client_id  || null,
        domain:     form.domain?.trim() || null,
        company_cnpj: form.company_cnpj || null,
      };
      // não re-envia a máscara como senha
      if (body.client_secret === MASK || body.client_secret === "") delete body.client_secret;

      if (editing) await updateIntegrationConfig(editing.id, body);
      else         await createIntegrationConfig(body);

      showSnack(editing ? "Configuração atualizada" : "Configuração criada");
      setDialogOpen(false);
      load();
    } catch (err) { showSnack(err.message, "error"); }
    finally       { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedIds.length) return;
    setSaving(true);
    try {
      await Promise.all(selectedIds.map(id => deleteIntegrationConfig(id)));
      showSnack(selectedIds.length > 1
        ? `${selectedIds.length} configurações excluídas`
        : "Configuração excluída");
      setDeleteOpen(false);
      setRowSel({ type: "include", ids: new Set() });
      load();
    } catch { showSnack("Erro ao excluir", "error"); }
    finally  { setSaving(false); }
  };

  const filtered = useMemo(() =>
    rows.filter(r =>
      !searchName || r.name?.toLowerCase().includes(searchName.trim().toLowerCase())
    ),
  [rows, searchName]);

  const f = field => ({
    value: form[field] ?? "",
    onChange: e => setForm(p => ({ ...p, [field]: e.target.value })),
  });

  const columns = [
    { field: "name",        headerName: "Nome",       flex: 1 },
    { field: "description", headerName: "Descrição",  flex: 1, valueGetter: (_, r) => r.description || "—" },
    { field: "company",     headerName: "Empresa",    flex: 1,
      valueGetter: (_, r) => r.company ? `${r.company.company_name} (${r.company.cnpj})` : "—" },
    { field: "direction",   headerName: "Direção",    width: 100,
      valueGetter: (_, r) => r.direction === "entrada" ? "Entrada" : "Saída" },
    { field: "url",         headerName: "URL",        flex: 2 },
    { field: "domain",      headerName: "Domínio (login)", width: 160,
      valueGetter: (_, r) => r.domain || "— (coringa)" },
    { field: "auth_type",   headerName: "Autenticação", width: 220,
      valueGetter: (_, r) => authTypeLabel(r.auth_type) },
    { field: "timeout_ms",  headerName: "Timeout (ms)", width: 130 },
    { field: "is_active",   headerName: "Ativo",      width: 80,
      valueGetter: (_, r) => r.is_active ? "Sim" : "Não" },
  ];

  const needsAuth  = form.auth_type !== "none";
  const needsToken = form.auth_type === "oauth2_client_credentials";

  return (
    <Box sx={{ p: 2, pt: 0 }}>
      <AdminPageHeader title="Configurações de Integração" />

      <AdminFilterPanel>
        <TextField label="Nome" size="small"
          value={searchName} onChange={e => setSearchName(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ ...filterFieldSx, width: 240 }} />
      </AdminFilterPanel>

      <Paper sx={ADMIN_PAPER_SX}>
        <DataGrid
          rows={filtered} columns={columns} getRowId={r => r.id}
          loading={loading} autoHeight checkboxSelection disableRowSelectionOnClick
          density="compact" showToolbar
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          rowSelectionModel={rowSel}
          onRowSelectionModelChange={m => setRowSel({ type: "include", ids: new Set(Array.from(m?.ids ?? [])) })}
          slots={{ toolbar: AdminGridToolbar }}
          slotProps={{ toolbar: {
            title: "Configurações Cadastradas",
            onNew: openNew, onEdit: openEdit, onDelete: () => setDeleteOpen(true),
            canEdit: selectedIds.length === 1, canDelete: selectedIds.length > 0,
          }}}
          sx={ADMIN_GRID_SX}
        />
      </Paper>

      {/* ── Formulário ── */}
      <AdminFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)}
        onSave={handleSave} saving={saving}
        title={editing ? "Editar Configuração" : "Nova Configuração"}>

        <TextField label="Nome *" {...f("name")} size="small"
          disabled={!!editing}
          helperText="Identificador único (ex: ias-sso-default, microsoft-sso-default)"
          InputLabelProps={{ shrink: true }} />

        <TextField label="Descrição" {...f("description")} size="small"
          InputLabelProps={{ shrink: true }} />

        <TextField select label="Direção *" size="small"
          value={form.direction} onChange={e => setForm(p => ({ ...p, direction: e.target.value }))}
          InputLabelProps={{ shrink: true }}>
          {DIRECTION_OPTIONS.map(d => (
            <MenuItem key={d} value={d}>{d === "entrada" ? "Entrada (inbound)" : "Saída (outbound)"}</MenuItem>
          ))}
        </TextField>

        <TextField label="URL *" {...f("url")} size="small"
          helperText="Endpoint principal da chamada"
          InputLabelProps={{ shrink: true }} />

        <TextField label="Domínio de e-mail" {...f("domain")} size="small"
          placeholder="suaempresa.com"
          helperText="Só para configs de SSO (microsoft-sso*, ias-sso*) — domínio do e-mail usado para resolver essa config no login. Deixe em branco para usar como fallback padrão."
          InputLabelProps={{ shrink: true }} />

        <TextField select label="Autenticação" size="small"
          value={form.auth_type} onChange={e => setForm(p => ({ ...p, auth_type: e.target.value }))}
          InputLabelProps={{ shrink: true }}>
          {AUTH_TYPE_OPTIONS.map(a => (
            <MenuItem key={a} value={a}>{authTypeLabel(a)}</MenuItem>
          ))}
        </TextField>

        <TextField label="Função RFC (role-sync SAP)" {...f("rfc_function")} size="small"
          helperText="Opcional. Só para configs de role-sync futuras — nome da RFC exposta no SAP"
          InputLabelProps={{ shrink: true }} />

        {needsToken && (
          <TextField label="Token URL (OAuth2)" {...f("token_url")} size="small"
            helperText="Endpoint para obter o access_token"
            InputLabelProps={{ shrink: true }} />
        )}

        {needsAuth && (
          <TextField
            label={needsToken ? "Client ID" : "Usuário (Basic)"}
            {...f("client_id")} size="small"
            InputLabelProps={{ shrink: true }} />
        )}

        {needsAuth && (
          <TextField
            label={needsToken ? "Client Secret" : "Senha (Basic)"}
            {...f("client_secret")}
            type={showSecret ? "text" : "password"}
            size="small"
            InputLabelProps={{ shrink: true }}
            helperText={editing ? "Deixe em branco para manter o valor atual" : ""}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowSecret(s => !s)}>
                    {showSecret ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        <TextField select label="Empresa" size="small"
          value={form.company_cnpj}
          onChange={e => setForm(p => ({ ...p, company_cnpj: e.target.value }))}
          InputLabelProps={{ shrink: true }}>
          <MenuItem value="">— Nenhuma (global) —</MenuItem>
          {companies.map(c => (
            <MenuItem key={c.cnpj} value={c.cnpj}>{c.company_name} — {c.cnpj}</MenuItem>
          ))}
        </TextField>

        <TextField label="Timeout (ms)" size="small"
          type="number" value={form.timeout_ms}
          onChange={e => setForm(p => ({ ...p, timeout_ms: e.target.value }))}
          InputLabelProps={{ shrink: true }} />

        {editing && (
          <FormControlLabel
            control={<Switch checked={!!form.is_active}
              onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />}
            label="Configuração ativa" />
        )}
      </AdminFormDialog>

      {/* ── Confirmar exclusão ── */}
      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)}
        maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: 0 } } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 14, pb: 1 }}>
          Confirmar exclusão
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {selectedIds.length > 1
              ? `Excluir ${selectedIds.length} configurações selecionadas?`
              : `Excluir a configuração "${selectedRow?.name}"?`}
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.2, gap: 1 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}
            sx={{ textTransform: "none", fontSize: 13 }}>Cancelar</Button>
          <Button onClick={handleDelete} disabled={saving}
            sx={{ textTransform: "none", fontSize: 13, color: "#d32f2f" }}>
            {saving ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(p => ({ ...p, open: false }))}>
        <Alert severity={snack.severity}
          onClose={() => setSnack(p => ({ ...p, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
